/**
 * SettingsPanel — Model configs, style presets, connected devices, and cloud sync status.
 */
import { useState } from 'react';
import { useQuery, useMutation } from 'convex/react';
import { api } from '../../convex/_generated/api';
import { useAppStore } from '@/store/appStore';
import {
  Settings,
  Plus,
  Cloud,
  Monitor,
  Copy,
  Check,
  Trash2,
  RefreshCw,
  Laptop,
  Wifi,
  WifiOff,
} from 'lucide-react';

const GOOGLE_DRIVE_SCOPE = 'https://www.googleapis.com/auth/drive.readonly';

export function SettingsPanel() {
  const accountId = useAppStore((s) => s.currentAccountId) ?? undefined;
  const currentUser = useQuery(api.users.getCurrentUser, {});
  const storageConfig = useQuery(api.storageConfig.get, { accountId });
  const effectiveAccountId = accountId ?? currentUser?._id ?? '';
  const [showAddConfig, setShowAddConfig] = useState(false);
  const [showAddPreset, setShowAddPreset] = useState(false);
  const [configProvider, setConfigProvider] = useState('Gemini');
  const [configBaseUrl, setConfigBaseUrl] = useState('https://generativelanguage.googleapis.com');
  const [presetName, setPresetName] = useState('');
  const [presetDesc, setPresetDesc] = useState('');

  const modelConfigs = useQuery(api.modelConfigs.list, { accountId });
  const stylePresets = useQuery(api.stylePresets.list, { accountId });
  const linkedDevices = useQuery(api.deviceLink.listLinkedDevices, {});
  const upsertConfig = useMutation(api.modelConfigs.upsert);
  const createPreset = useMutation(api.stylePresets.create);
  const revokeDevice = useMutation(api.deviceLink.revokeDevice);

  const handleAddConfig = async () => {
    try {
      await upsertConfig({
        accountId,
        provider: configProvider,
        base_url: configBaseUrl,
        is_active: true,
      });
      setShowAddConfig(false);
    } catch (err) {
      console.error('Add config failed:', err);
    }
  };

  const handleAddPreset = async () => {
    if (!presetName.trim()) return;
    try {
      await createPreset({
        accountId,
        name: presetName.trim(),
        description: presetDesc.trim() || 'Custom style',
      });
      setPresetName('');
      setPresetDesc('');
      setShowAddPreset(false);
    } catch (err) {
      console.error('Add preset failed:', err);
    }
  };

  const handleRevokeDevice = async (deviceId: string) => {
    try {
      await revokeDevice({ deviceId: deviceId as any });
    } catch (err) {
      console.error('Revoke device failed:', err);
    }
  };

  return (
    <div className="flex h-full flex-col overflow-hidden bg-background">
      <div className="flex-shrink-0 border-b border-border p-4 bg-card/30">
        <h2 className="font-semibold text-foreground flex items-center gap-2">
          <Settings className="h-4 w-4 text-muted-foreground" />
          Settings
        </h2>
        <p className="text-xs text-muted-foreground mt-0.5">
          Model configuration, style presets, and connected devices
        </p>
      </div>
      <div className="flex-1 overflow-auto p-4 space-y-6">
        {/* Connected Devices & Cloud Sync */}
        <section>
          <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
            <div className="h-6 w-6 rounded-md bg-blue-500/15 flex items-center justify-center">
              <Monitor className="h-3.5 w-3.5 text-blue-400" />
            </div>
            Connected Devices
          </h3>
          <p className="text-xs text-muted-foreground mb-3 ml-8">
            Desktop apps linked to your cloud account. Open the desktop app and click <span className="text-foreground font-medium">Settings → Connect to Cloud</span>.
          </p>

          {linkedDevices === undefined ? (
            <div className="flex items-center justify-center py-4">
              <div className="h-4 w-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : linkedDevices.length === 0 ? (
            <div className="rounded-xl border border-dashed border-border bg-card/50 p-5 text-center">
              <div className="h-12 w-12 rounded-2xl bg-muted/50 flex items-center justify-center mx-auto mb-2">
                <Laptop className="h-6 w-6 text-muted-foreground/40" />
              </div>
              <p className="text-sm text-muted-foreground font-medium">No desktop apps connected</p>
              <p className="text-xs text-muted-foreground mt-1">
                Open the desktop app → Settings → Connect to Cloud
              </p>
            </div>
          ) : (
            <ul className="space-y-2">
              {linkedDevices.map((device: any) => (
                <li key={device.id} className="rounded-xl border border-border bg-card p-3 flex items-center gap-3 transition-all hover:border-primary/20">
                  <div className="flex-shrink-0">
                    <div className="h-9 w-9 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Laptop className="h-4 w-4 text-primary" />
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium text-sm truncate">{device.deviceName}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-1.5 mt-0.5">
                      <div className="h-1.5 w-1.5 rounded-full bg-green-500" />
                      Last synced {formatRelativeTime(device.lastUsedAt)}
                    </div>
                  </div>
                  <button
                    onClick={() => handleRevokeDevice(device.id)}
                    className="rounded-lg p-1.5 text-muted-foreground hover:text-destructive hover:bg-destructive/10 transition-all"
                    title="Disconnect device"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Model configs */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <div className="h-6 w-6 rounded-md bg-emerald-500/15 flex items-center justify-center">
                <Settings className="h-3.5 w-3.5 text-emerald-400" />
              </div>
              Model configs
            </h3>
            <button
              onClick={() => setShowAddConfig(!showAddConfig)}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
          {showAddConfig && (
            <div className="rounded-xl border border-border bg-card p-3 mb-2 space-y-2 animate-fade-in">
              <input
                placeholder="Provider"
                value={configProvider}
                onChange={(e) => setConfigProvider(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all"
              />
              <input
                placeholder="Base URL"
                value={configBaseUrl}
                onChange={(e) => setConfigBaseUrl(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all"
              />
              <button onClick={handleAddConfig} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors">Save</button>
            </div>
          )}
          {modelConfigs === undefined ? (
            <div className="flex items-center justify-center py-4">
              <div className="h-4 w-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : modelConfigs.length === 0 && !showAddConfig ? (
            <div className="rounded-xl border border-dashed border-border bg-card/50 p-4 text-center">
              <p className="text-sm text-muted-foreground">No model configs yet</p>
              <p className="text-xs text-muted-foreground mt-0.5">Add one to configure AI providers</p>
            </div>
          ) : (
            <ul className="space-y-2">
              {modelConfigs.map((m: any) => (
                <li key={m._id} className="rounded-xl border border-border bg-card p-3 transition-all hover:border-primary/20">
                  <div className="font-medium text-sm">{m.provider}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{m.model ?? m.engine ?? m.base_url}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Style presets */}
        <section>
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-sm font-medium flex items-center gap-2">
              <div className="h-6 w-6 rounded-md bg-purple-500/15 flex items-center justify-center">
                <span className="text-xs">🎨</span>
              </div>
              Style presets
            </h3>
            <button
              onClick={() => setShowAddPreset(!showAddPreset)}
              className="flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-medium text-primary hover:bg-primary/10 transition-colors"
            >
              <Plus className="h-3.5 w-3.5" /> Add
            </button>
          </div>
          {showAddPreset && (
            <div className="rounded-xl border border-border bg-card p-3 mb-2 space-y-2 animate-fade-in">
              <input
                placeholder="Name"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all"
              />
              <input
                placeholder="Description"
                value={presetDesc}
                onChange={(e) => setPresetDesc(e.target.value)}
                className="w-full rounded-lg border border-input bg-background px-3 py-1.5 text-sm focus:ring-2 focus:ring-primary/40 focus:border-primary/40 transition-all"
              />
              <button onClick={handleAddPreset} disabled={!presetName.trim()} className="rounded-lg bg-primary px-3 py-1.5 text-sm font-medium text-primary-foreground disabled:opacity-50 hover:bg-primary/90 transition-colors">Save</button>
            </div>
          )}
          {stylePresets === undefined ? (
            <div className="flex items-center justify-center py-4">
              <div className="h-4 w-4 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
            </div>
          ) : stylePresets.length === 0 && !showAddPreset ? (
            <div className="text-sm text-muted-foreground ml-8">No style presets yet. Click Add to create one.</div>
          ) : (
            <ul className="space-y-2">
              {stylePresets.map((p: any) => (
                <li key={p._id} className="rounded-xl border border-border bg-card p-3 transition-all hover:border-primary/20">
                  <div className="font-medium text-sm">{p.name}</div>
                  <div className="text-xs text-muted-foreground mt-0.5">{p.description}</div>
                </li>
              ))}
            </ul>
          )}
        </section>

        {/* Google Drive */}
        <section>
          <h3 className="text-sm font-medium mb-2 flex items-center gap-2">
            <div className="h-6 w-6 rounded-md bg-amber-500/15 flex items-center justify-center">
              <Cloud className="h-3.5 w-3.5 text-amber-400" />
            </div>
            Google Drive
          </h3>
          <div className="ml-8">
            {storageConfig?.googleDriveLinked ? (
              <p className="text-xs text-muted-foreground mb-2">Drive linked. Import from Drive in Assets.</p>
            ) : (
              <p className="text-xs text-muted-foreground mb-2">
                Link your account to import assets from Google Drive.
              </p>
            )}
            <button
              type="button"
              disabled={!import.meta.env.VITE_GOOGLE_CLIENT_ID || !effectiveAccountId || !!storageConfig?.googleDriveLinked}
              onClick={() => {
                if (!import.meta.env.VITE_GOOGLE_CLIENT_ID || !effectiveAccountId) return;
                const redirectUri = `${window.location.origin}/oauth/google-drive-callback`;
                const url = `https://accounts.google.com/o/oauth2/v2/auth?client_id=${encodeURIComponent(import.meta.env.VITE_GOOGLE_CLIENT_ID)}&redirect_uri=${encodeURIComponent(redirectUri)}&response_type=code&scope=${encodeURIComponent(GOOGLE_DRIVE_SCOPE)}&state=${encodeURIComponent(effectiveAccountId)}&access_type=offline&prompt=consent`;
                window.location.href = url;
              }}
              className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-sm font-medium bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 disabled:pointer-events-none transition-all active:scale-[0.98]"
            >
              {storageConfig?.googleDriveLinked ? (
                <>
                  <Check className="h-4 w-4" />
                  Drive linked
                </>
              ) : (
                'Link Google Drive'
              )}
            </button>
            {!import.meta.env.VITE_GOOGLE_CLIENT_ID && (
              <p className="text-[11px] text-muted-foreground mt-1.5">Set VITE_GOOGLE_CLIENT_ID to enable.</p>
            )}
          </div>
        </section>
      </div>
    </div>
  );
}

/** Format a timestamp as relative time (e.g., "2 minutes ago") */
function formatRelativeTime(timestamp: number): string {
  const diff = Date.now() - timestamp;
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}
